import { Avatar, Dropdown, Layout, MenuProps, Steps, message } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import AddCorpInfoForm from "./corp-info";
import AddCorpBranchForm from "./corp-branch";
import { NewCorpEntity } from "./corp-new-entity";
import CorpSummary from "./corp-summary";
import { ReactComponent as IconWhite } from "../../images/Icon_White.svg";
import StripePricingPage from "./corp-pricing-table";
import { pricingTable } from "../../constants";
import "./corp-new-layout.css";
import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { Header, Content } from "antd/es/layout/layout";
import { CompanyContext } from "../../contexts/company-context";
import { AuthenticationContext } from "../../contexts/authentication-context";
import { useAccount, useMsal } from "@azure/msal-react";
import { logout } from "../../Util/logout";
import { CorporationOperation } from "../../serviceoperation/CorporationOperation";

interface NewCorpLayoutFormProps {
  onCreate?: (values: NewCorpEntity) => void;
}

const NewCorporationLayout: React.FC<NewCorpLayoutFormProps> = () => {
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const { selectedCompany, companyData, updateSelectedCompany } =
    useContext(CompanyContext);
  const shouldOpenPlanSelectionOnly =
    !!selectedCompany?.corporationId && selectedCompany.planSelected === false;
  const defaultSelectedKey = shouldOpenPlanSelectionOnly ? 3 : 0;
  const [currentStep, setCurrentStep] = useState<number>(defaultSelectedKey);
  const [newCorpData, setNewCorpData] = useState<NewCorpEntity>({});
  const [clientRefId, setClientRefId] = useState<string>();

  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const corpOps = useMemo(() => new CorporationOperation(), []);

  const handleLogout = () => logout(instance, account);

  const companyMenu: MenuProps["items"] = companyData.map((company) => ({
    label: company.name,
    key: company.corporationId.toString(),
  }));

  const handleCompanyClick: MenuProps["onClick"] = async ({ key }) => {
    const companyId = Number.parseInt(key, 10);
    const switchingMessageKey = "switching-corporation";

    message.loading({
      content: "Switching corporation...",
      key: switchingMessageKey,
      duration: 0,
    });

    updateSelectedCompany(companyId);

    try {
      const corpResult = await corpOps.getCorpByIdAsync(companyId);
      if (corpResult.statusCode === 200 && corpResult.data) {
        updateSelectedCompany(companyId, corpResult.data);

        if (corpResult.data.planSelected === false) {
          setCurrentStep(3);
          setClientRefId(corpResult.data.corporationId.toString());
          message.success({
            content: "Corporation switched.",
            key: switchingMessageKey,
            duration: 1.2,
          });
          return;
        }

        window.location.assign("/home");
        return;
      }
    } catch (error) {
      // Fall back to cached company info when detail lookup fails.
    }

    const selected = companyData.find(
      (company) => company.corporationId === companyId,
    );
    if (selected?.planSelected === false) {
      setCurrentStep(3);
      setClientRefId(selected.corporationId.toString());
      message.success({
        content: "Corporation switched.",
        key: switchingMessageKey,
        duration: 1.2,
      });
    } else {
      window.location.assign("/home");
    }

    message.destroy(switchingMessageKey);
  };

  useEffect(() => {
    if (shouldOpenPlanSelectionOnly && selectedCompany?.corporationId) {
      setCurrentStep(3);
      setClientRefId(selectedCompany.corporationId.toString());
    }
  }, [shouldOpenPlanSelectionOnly, selectedCompany]);

  const saveNewCorpData = useCallback(
    (data: any) => {
      setNewCorpData(data);
      console.log(data);
      next();
      //setScheduleData(data);
      //SetEmployee(employeeData);
      //setEmployeeDetails(employeeDetails);
      //setSelectedWeek(selectedWeek);
      //setNextDisabled(data.length > 0 ? false : true);
    },
    [currentStep],
  );

  const looksGood = (values: NewCorpEntity, response: any) => {
    console.log(response.data.corporationID);
    setClientRefId(response.data.corporationID);
    // Set the next step so that we can to sbscription page.
    next();
    //window.location.href = "/";
  };

  const handleStepChange = (value: number) => {
    setCurrentStep(value);
  };

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const submitSchedule = () => {
    message.success("Schedule successfully published!");
  };

  const renderComponent = () => {
    switch (currentStep) {
      case 0:
        return (
          <AddCorpInfoForm
            newCorpData={newCorpData}
            onCreate={saveNewCorpData}
          />
        );
      case 1:
        return (
          <AddCorpBranchForm
            newCorpData={newCorpData}
            onCreate={saveNewCorpData}
          />
        );
      case 2:
        return <CorpSummary newCorpData={newCorpData} onCreate={looksGood} />;
      case 3:
        return (
          <StripePricingPage
            key={
              clientRefId ??
              selectedCompany?.corporationId?.toString() ??
              "join-pricing"
            }
            pricingTableId={pricingTable.Id}
            publishableKey={pricingTable.publishableKey}
            referenceId={clientRefId ?? ""}
          />
        );
    }
  };

  return (
    <Layout className="app-shell">
      {/* Top nav */}
      <Header className="app-header">
        <div className="brand">
          <IconWhite width={28} height={28} />
          <span className="brand-name">myTimeverse</span>
        </div>
        <div className="header-actions">
          <Dropdown.Button
            className="join-company-switcher"
            menu={{
              items: companyMenu,
              onClick: handleCompanyClick,
              selectedKeys: selectedCompany?.corporationId
                ? [selectedCompany.corporationId.toString()]
                : [],
            }}
          >
            <span className="join-company-switcher-label">
              {selectedCompany?.name ?? "Select Organization"}
            </span>
          </Dropdown.Button>

          <Dropdown
            menu={{
              items: [
                {
                  key: "profile",
                  label: "Profile",
                  onClick: () => navigate("/profile"),
                },
                {
                  key: "logout",
                  icon: <LogoutOutlined />,
                  label: "Logout",
                  onClick: handleLogout,
                },
              ],
            }}
          >
            <span className="join-user-avatar-trigger">
              <Avatar style={{ cursor: "pointer" }} icon={<UserOutlined />}>
                {userInfo?.firstName?.[0]}
              </Avatar>
            </span>
          </Dropdown>
        </div>
      </Header>

      {/* Left scrollable content */}
      <Content className="app-content">
        <div className="content-scroll">{renderComponent()}</div>
      </Content>

      {/* Right rail with stepper + footer inside */}
      <aside className="right-rail">
        <div className="steps-scroll">
          <Steps
            direction="vertical"
            current={currentStep}
            onChange={handleStepChange}
            className="right-rail-steps"
            items={[
              {
                title: "Step 1",
                subTitle: "Account Details",
                description: "Tell us a little about you and your corporation.",
                disabled: true,
              },
              {
                title: "Step 2",
                subTitle: "Branch & Department",
                description: "Configure your locations.",
                disabled: true,
              },
              {
                title: "Step 3",
                subTitle: "Summary",
                description: "You're almost there.",
                disabled: true,
              },
            ]}
          />
        </div>
        <div className="right-footer">
          © {new Date().getFullYear()} myTimeverse. All rights reserved.
        </div>
      </aside>
    </Layout>
  );
};

export default NewCorporationLayout;
